import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Button,
  Stack,
  TextField,
  MenuItem,
  Chip,
  Fade,
  Skeleton,
  Card,
  CardContent,
  IconButton,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip
} from '@mui/material';
import { useAuth } from '../context/AuthContext';
import PaperCard from '../components/PaperCard';
import { APP_DOWNLOAD } from '../config/appConfig';
import { loadPreviousPapers } from '../lib/loadPreviousPapers';
import { filterPapers } from '../lib/filterPapers';
import { prepareExamData } from '../lib/examHelpers';
import { 
  Search as SearchIcon,
  School as SchoolIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import ExamEnvironment from './ExamEnvironment';
import PageLayoutWithAds from '../components/layout/PageLayoutWithAds';
import AdminEditPaperModal from '../components/admin/AdminEditPaperModal';
import { deletePaper } from '../services/indianExamsService';

// ...existing code...

// Simple styling matching ExamsCatalog design
// No custom themes needed - using Material-UI defaults

const PreviousYearPapers = () => {
  const navigate = useNavigate();
  const [papers, setPapers] = useState([]);
  const [examModal, setExamModal] = useState({ open: false, paper: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [selectedExam, setSelectedExam] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedYear, setSelectedYear] = useState('');

  // Admin state
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [editModal, setEditModal] = useState({ open: false, paper: null });
  const [deleteModal, setDeleteModal] = useState({ open: false, paper: null });
  const [isDeleting, setIsDeleting] = useState(false);

  // Fetch previous year papers using shared util
  useEffect(() => {
    let mounted = true;
    async function fetchPapers() {
      setLoading(true);
      setError('');
      try {
        const papers = await loadPreviousPapers();
        if (mounted) setPapers(papers);
      } catch (e) {
        if (mounted) setError('Failed to load papers. Please try again.');
      } finally {
        if (mounted) setLoading(false);
      }
    }
    fetchPapers();
    return () => { mounted = false; };
  }, []);

  // Filtering logic using shared util
  const filteredPapers = useMemo(() => {
    return filterPapers(papers, {
      examId: selectedExam,
      category: selectedCategory,
      year: selectedYear,
      search
    });
  }, [papers, selectedExam, selectedCategory, selectedYear, search]);

  // Get unique values for filters
  const uniqueExams = [...new Set(papers.map(paper => paper.examId))];
  const uniqueCategories = [...new Set(papers.map(paper => paper.category))];
  const uniqueYears = [...new Set(papers.map(paper => paper.year))].sort((a, b) => b - a);

  const handleClearFilters = () => {
    setSelectedExam('');
    setSelectedCategory('all');
    setSelectedYear('');
    setSearch('');
  };

  const handleDeletePaper = async () => {
    if (!deleteModal.paper) return;
    setIsDeleting(true);
    try {
      const res = await deletePaper(deleteModal.paper.examId, deleteModal.paper.id || deleteModal.paper.paperId);
      if (res && res.success) {
        setPapers(prev => prev.filter(p => (p.id || p.paperId) !== (deleteModal.paper.id || deleteModal.paper.paperId)));
      } else {
        alert(res?.error || 'Failed to delete paper');
      }
    } catch (e) {
      alert('Error deleting paper: ' + e.message);
    } finally {
      setIsDeleting(false);
      setDeleteModal({ open: false, paper: null });
    }
  };

  const handleSaveEdit = (updatedPaper) => {
    setPapers(prev => prev.map(p => (p.id || p.paperId) === (updatedPaper.id || updatedPaper.paperId) ? updatedPaper : p));
    setEditModal({ open: false, paper: null });
  };

  const PaperCardSkeleton = () => (
    <Grid item xs={12} sm={6} md={4}>
      <Card sx={{ height: 320, borderRadius: 3 }}>
        <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Skeleton variant="text" width="60%" height={20} />
          <Skeleton variant="text" width="100%" height={32} sx={{ mt: 1 }} />
          <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
            <Skeleton variant="rectangular" width={60} height={24} sx={{ borderRadius: 1 }} />
            <Skeleton variant="rectangular" width={80} height={24} sx={{ borderRadius: 1 }} />
          </Stack>
          <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
            <Skeleton variant="text" width={50} />
            <Skeleton variant="text" width={70} />
          </Stack>
          <Skeleton variant="rectangular" width="100%" height={36} sx={{ mt: 'auto', borderRadius: 2 }} />
        </CardContent>
      </Card>
    </Grid>
  );

  return (
    <PageLayoutWithAds 
      showTopBanner={true}
      showSidebar={true}
      showBottomSticky={true}
      maxWidth="xl"
    >
      {/* Hero Section */}
      <Box sx={{ mb: 6, textAlign: 'center' }}>

        <Typography 
          variant="h6" 
          color="text.secondary" 
          sx={{ 
            maxWidth: 700, 
            mx: 'auto',
            lineHeight: 1.6,
            fontWeight: 400
          }}
        >
          Master your exams with authentic question papers from previous years. 
          Practice with real exam formats and boost your confidence.
        </Typography>
      </Box>

      {/* Advanced Filter Section */}
      <Card 
        elevation={0} 
        sx={{ 
          mb: 4, 
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'visible'
        }}
      >
        <CardContent sx={{ p: 3 }}>
          {/* Filter Header */}
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <FilterIcon sx={{ color: 'primary.main' }} />
              <Typography variant="h6" fontWeight={700} color="primary.main">
                Find Your Papers
              </Typography>
              <Divider orientation="vertical" flexItem />
              <Chip 
                label={`${filteredPapers.length} papers found`}
                color="primary"
                variant="outlined"
                size="small"
              />
            </Stack>
            {isAdmin && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate('/import')}
                sx={{ borderRadius: 2 }}
              >
                Import Papers
              </Button>
            )}
          </Stack>
          
          <Grid container spacing={3} alignItems="end">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Search papers"
                placeholder="Search by title, exam, or category..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />,
                  endAdornment: search && (
                    <IconButton size="small" onClick={() => setSearch('')}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  )
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main',
                    }
                  }
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                select
                size="small"
                label="Exam"
                value={selectedExam}
                onChange={(e) => setSelectedExam(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              >
                <MenuItem value="">All Exams</MenuItem>
                {uniqueExams.map((exam) => (
                  <MenuItem key={exam} value={exam}>{exam}</MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                select
                size="small"
                label="Category"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              >
                <MenuItem value="all">All Categories</MenuItem>
                {uniqueCategories.map((category) => (
                  <MenuItem key={category} value={category}>{category}</MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                select
                size="small"
                label="Year"
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              >
                <MenuItem value="">All Years</MenuItem>
                {uniqueYears.map((year) => (
                  <MenuItem key={year} value={year}>{year}</MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleClearFilters}
                startIcon={<ClearIcon />}
                sx={{ 
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  py: 1.2
                }}
              >
                Clear All
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Content Section */}
      {loading ? (
        <Grid container spacing={3}>
          {[...Array(6)].map((_, index) => (
            <PaperCardSkeleton key={index} />
          ))}
        </Grid>
      ) : error ? (
        <Card sx={{ textAlign: 'center', borderRadius: 3 }}>
          <CardContent sx={{ p: 6 }}>
            <SchoolIcon sx={{ fontSize: 48, color: 'error.main', mb: 2 }} />
            <Typography color="error" variant="h6" sx={{ mb: 2 }}>
              {error}
            </Typography>
            <Button 
              variant="contained" 
              onClick={() => window.location.reload()}
              sx={{ borderRadius: 2 }}
            >
              Try Again
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Fade in={true}>
          <Grid container spacing={3}>
            {filteredPapers.length === 0 ? (
              <Grid item xs={12}>
                <Card sx={{ textAlign: 'center', borderRadius: 3 }}>
                  <CardContent sx={{ p: 6 }}>
                    <SchoolIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                      No papers found
                    </Typography>
                    <Typography color="text.secondary" sx={{ mb: 3 }}>
                      Try adjusting your filters to find more papers
                    </Typography>
                    <Button
                      variant="outlined"
                      onClick={handleClearFilters}
                      startIcon={<ClearIcon />}
                      sx={{ borderRadius: 2 }}
                    >
                      Clear All Filters
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ) : (
              filteredPapers.map((paper, idx) => {
                return (
                  <Grid 
                    item 
                    xs={12} 
                    sm={6} 
                    md={4} 
                    lg={3} 
                    key={paper.id}
                  >
                    <PaperCard
                      paper={paper}
                      index={idx}
                      variant="previous"
                      onClick={() => setExamModal({ open: true, paper })}
                      showAppDownloadQr={APP_DOWNLOAD.enabled}
                      playStoreUrl={APP_DOWNLOAD.playStoreUrl}
                      qrImageUrl={APP_DOWNLOAD.qrImageUrl}
                      qrSize={APP_DOWNLOAD.qrSize}
                      actionButtonText="Start Practice"
                      showDescription={false}
                      showTotalMarks={false}
                      customFields={[
                        {
                          icon: <SchoolIcon fontSize="small" color="action" />,
                          text: paper.examId || 'Unknown Exam'
                        }
                      ]}
                    />
                    {isAdmin && (
                      <Stack direction="row" spacing={1} sx={{ mt: 1, justifyContent: 'flex-end' }}>
                        <Tooltip title="Edit Metadata">
                          <IconButton size="small" onClick={() => setEditModal({ open: true, paper })}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Paper">
                          <IconButton size="small" color="error" onClick={() => setDeleteModal({ open: true, paper })}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    )}
                  </Grid>
                );
              })
            )}
          </Grid>
        </Fade>
      )}
    {/* Exam Modal */}
    {examModal.open && (
      <Box 
        sx={{ 
          position: 'fixed', 
          zIndex: 20000, 
          top: 0, 
          left: 0, 
          width: '100vw', 
          height: '100vh', 
          bgcolor: 'rgba(0, 0, 0, 0.8)',
          backdropFilter: 'blur(4px)'
        }}
      >
        <Box sx={{ 
          position: 'absolute', 
          top: 0, 
          left: 0, 
          width: '100vw', 
          height: '100vh', 
          overflow: 'auto' 
        }}>
          <ExamEnvironment
            {...prepareExamData(examModal.paper, { name: 'Candidate' })}
            onClose={() => setExamModal({ open: false, paper: null })}
          />
        </Box>
      </Box>
    )}

    {/* Admin Delete Confirmation */}
    <Dialog open={deleteModal.open} onClose={() => !isDeleting && setDeleteModal({ open: false, paper: null })}>
      <DialogTitle>Delete Paper?</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Are you sure you want to delete <strong>{deleteModal.paper?.title}</strong>? This action cannot be undone.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setDeleteModal({ open: false, paper: null })} disabled={isDeleting}>Cancel</Button>
        <Button onClick={handleDeletePaper} color="error" variant="contained" disabled={isDeleting}>
          {isDeleting ? 'Deleting...' : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>

    {/* Admin Edit Modal */}
    {editModal.open && (
      <AdminEditPaperModal
        open={editModal.open}
        paper={editModal.paper}
        onClose={() => setEditModal({ open: false, paper: null })}
        onSave={handleSaveEdit}
      />
    )}
    </PageLayoutWithAds>
  );
};

export default PreviousYearPapers;
